// @ts-nocheck
// app/quiz/index.tsx
// ✅ v3.1 — Fix: progress dot is perfectly synced with progress bar (pixel-perfect)

import {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
  Platform,
  Modal,
  TextInput,
  KeyboardAvoidingView,
  ActivityIndicator,
  TouchableOpacity,
  Animated,
  Vibration,
  I18nManager,
} from 'react-native';
import { Stack, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { Asset } from 'expo-asset';
import { SvgUri } from 'react-native-svg';
import { supabase } from '@/utils/supabase';
import { theme } from '@/constants/theme';
import { useAccessibility } from '@/contexts/AccessibilityContext';
import {
  trackPageView,
  trackButtonClick,
  trackFunnelStep,
} from '@/utils/analytics';
import { usePageEngagement } from '@/hooks/usePageEngagement';
import {
  getAccessibleBackgroundColor,
  getAccessibleColor,
  getAccessibleTextColor,
  getAccessibleFontFamily,
  shouldReduceMotion,
} from '@/utils/accessibilityHelpers';

// ─── Types ────────────────────────────────────────────────────────────────────

type TrackKey =
  | 'programming'
  | 'data_ai'
  | 'digital_marketing'
  | 'ui_ux'
  | 'stock_market'
  | 'psychometric'
  | 'business_management'
  | 'content_creation'
  | 'customer_service'
  | 'cyber'
  | 'cloud_devops';

type Track = {
  key: TrackKey;
  title: string;
  subtitle: string;
  bullets: string[];
  href: string;
  chatSeed: string;
  emoji: string;
  color: string;
  gradient: [string, string];
  persona: string;
};

type Option = {
  label: string;
  emoji?: string;
  persona?: string; // e.g. "הדרך של המפתחים"
  weights: Partial<Record<TrackKey, number>>;
};

type Question = {
  id: string;
  title: string;
  subtitle?: string;
  options: Option[];
};

// ─── Validation ───────────────────────────────────────────────────────────────

function normalizeILPhone(raw: string): string {
  let p = (raw ?? '').toString().trim();
  p = p.replace(/[^\d+]/g, '');
  if (p.startsWith('+')) p = p.slice(1);
  if (p.startsWith('972') && p.length === 12) p = '0' + p.slice(3);
  return p;
}

function isValidILPhone(raw: string): boolean {
  const p = normalizeILPhone(raw);
  return /^05\d{8}$/.test(p);
}

function isValidEmail(raw: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((raw ?? '').trim().toLowerCase());
}

// ─── Local storage ────────────────────────────────────────────────────────────

const LEAD_STORAGE_KEY = 'kursi_lead_v3';
const LEAD_TTL_MS = 1000 * 60 * 60 * 24 * 14;

function safeReadStoredLead(): {
  fullName: string;
  phone: string;
  email: string;
  consent: boolean;
} | null {
  try {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(LEAD_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const fullName = (parsed?.fullName ?? '').toString().trim();
    const phone = normalizeILPhone(parsed?.phone ?? '');
    const email = (parsed?.email ?? '').toString().trim().toLowerCase();
    const consent = !!parsed?.consent;
    const savedAt = Number(parsed?.savedAt ?? 0);
    if (!fullName || fullName.length < 2) return null;
    if (!isValidILPhone(phone) && !isValidEmail(email)) return null;
    if (savedAt && Date.now() - savedAt > LEAD_TTL_MS) {
      window.localStorage.removeItem(LEAD_STORAGE_KEY);
      return null;
    }
    return { fullName, phone, email, consent };
  } catch {
    return null;
  }
}

function safeWriteStoredLead(
  fullName: string,
  phone: string,
  email: string,
  consent: boolean
) {
  try {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    window.localStorage.setItem(
      LEAD_STORAGE_KEY,
      JSON.stringify({
        fullName: fullName.trim(),
        phone: normalizeILPhone(phone),
        email: email.trim().toLowerCase(),
        consent: !!consent,
        savedAt: Date.now(),
      })
    );
  } catch {}
}

// ─── Rate limiting ────────────────────────────────────────────────────────────

const lastSubmitRef = { ts: 0 };
function canSubmit(): boolean {
  const now = Date.now();
  if (now - lastSubmitRef.ts < 8000) return false;
  lastSubmitRef.ts = now;
  return true;
}

// ─── Scoring ──────────────────────────────────────────────────────────────────

function getLevelByZScore(score: number, allScores: number[]): string {
  const n = allScores.length;
  const mean = allScores.reduce((a, b) => a + b, 0) / n;
  const variance =
    allScores.map((s) => (s - mean) ** 2).reduce((a, b) => a + b, 0) / n;
  const std = Math.sqrt(variance) || 1;
  const z = (score - mean) / std;
  if (z > 1.2) return 'גבוהה מאוד';
  if (z > 0.4) return 'גבוהה';
  if (z > -0.4) return 'בינונית';
  return 'נמוכה';
}

function sanitizeAnswers(answers: Record<string, string>): Record<string, string> {
  const safe: Record<string, string> = {};
  for (const [k, v] of Object.entries(answers)) {
    safe[k] = (v ?? '').toString().slice(0, 200);
  }
  return safe;
}

// ─── Share helper ─────────────────────────────────────────────────────────────

async function shareQuizResult(trackTitle: string): Promise<boolean> {
  const message = `גיליתי שאני מתאים ל${trackTitle}! גלה גם את המקצוע שמתאים לך 🎯`;
  const url = 'https://kursi.co/quiz';
  if (Platform.OS !== 'web') {
    try {
      const { Share } = await import('react-native');
      await Share.share({ message: `${message} ${url}` });
      return false;
    } catch {}
  }
  if (
    Platform.OS === 'web' &&
    typeof navigator !== 'undefined' &&
    (navigator as any).share &&
    typeof (navigator as any).canShare === 'function'
  ) {
    try {
      await (navigator as any).share({ title: 'שאלון המקצוע של Kursi', text: message, url });
      return false;
    } catch {}
  }
  if (typeof navigator !== 'undefined' && (navigator as any).clipboard) {
    try {
      await (navigator as any).clipboard.writeText(`${message} ${url}`);
      return true; // copied
    } catch {}
  }
  return false;
}

// ─── Live feedback map ────────────────────────────────────────────────────────
// Keys must match the exact option labels defined in the questions array below.

const LIVE_FEEDBACK: Record<string, string> = {
  // Q1: energy_source
  'כשאני בונה משהו מאפס': '🔨 בחרת כמו אנשי פיתוח — הם רוצים לבנות, לא רק לדעת',
  'כשאני מגלה תובנה שאחרים פיספסו': '🔍 בחרת כמו אנליסטים — הם רואים מה שאחרים מפספסים',
  'כשאני גורם למישהו להצליח': '🌟 בחרת כמו מנהלים ומאמנים — האנרגיה שלהם מגיעה מאחרים',
  'כשאני יוצר משהו שמרגש אנשים': '✨ בחרת כמו מעצבים — אסתטיקה היא שפה בשבילם',
  'כשאני מגן על מערכות ומתמודד עם אתגרים טכניים': '🔒 בחרת כמו אנשי סייבר — הם אוהבים להיות צעד לפני התוקפים',
  // Q2: superpower
  'לפתור בעיות טכניות שמבלבלות אחרים': '🛠️ בחרת כמו מפתחים — הם אוהבים שיש להם בעיה לפתור',
  'להסביר דברים מורכבים בצורה פשוטה': '💡 בחרת כמו מורים ומנהלים — הם מתרגמים עולמות',
  'לעצב ולמצוא פתרון ויזואלי מושלם': '🎨 בחרת כמו אמנים דיגיטליים — עין שלהם שווה הרבה',
  'לנתח מצב ולהחליט נכון תחת לחץ': '📊 בחרת כמו אנליסטים — נתונים הם הדלק שלהם',
  'לאבטח ולהגן על מערכות מפני איומים': '🛡️ בחרת כמו מומחי סייבר — הם שומרים על הדיגיטל בטוח',
  // Q3: problem_type
  'חידה לוגית שיש לה פתרון אחד נכון': '🧩 בחרת כמו מפתחים — הלוגיקה היא המשחק שלהם',
  'בעיה אנושית שאין לה תשובה מושלמת': '💬 בחרת כמו אנשי שירות — הם פורחים מאינטראקציה',
  'איך להגיע ליעד בצורה הכי חכמה': '🎯 בחרת כמו מנהלים — יעילות היא הדת שלהם',
  'איך לגרום למשהו להיראות ולהרגיש טוב יותר': '✨ בחרת כמו מעצבים — הם לא יכולים לא לשפר',
  'חולשת אבטחה שצריך לגלות ולסגור לפני שמישהו מנצל אותה': '🔓 בחרת כמו האקרים אתיים — הם מוצאים חורים לפני הרעים',
  // Q4: work_values
  'חופש — לעבוד מתי ואיך שאני רוצה': '🕊️ בחרת כמו פרילנסרים — הם לא מסוגלים בתוך קופסה',
  'השפעה — לדעת שמה שאני עושה משנה': '🤝 בחרת כמו אנשים שמניעים עולמות — בדרך עדינה',
  'כסף — צמיחה כלכלית מהירה ומשמעותית': '💰 בחרת כמו יזמים — תוצאות הן מה שסופרים',
  'יצירה — ביטוי עצמי בכל מה שאני עושה': '🎭 בחרת כמו אנשי תוכן — הם חייבים ליצור',
  'יציבות — מסגרת ברורה ודרך מוגדרת': '🏛️ בחרת כמו אנשים שמוצאים חופש בתוך סדר',
  // Q5: self_description
  '"אני אוהב לנפח בעיות עד שהן נפתרות"': '🔬 בחרת כמו מדענים ואנליסטים — הם לא עוצרים עד שמבינים',
  '"אני מדבר עם כולם ואנשים פותחים איתי"': '🗣️ בחרת כמו אנשי שירות — הקשר האנושי הוא העוצמה שלהם',
  '"אני רואה פוטנציאל יצירתי בכל מקום"': '🎨 בחרת כמו יוצרים — הם ממציאים מחדש כל דבר שהם נוגעים בו',
  '"אני בונה מערכות ותוכניות לכל דבר"': '📋 בחרת כמו מנהלים — הם גורמים לכאוס להפוך לסדר',
  '"אני לא מפחד לסכן — הסיכון מרגש אותי"': '🦁 בחרת כמו סוחרים ויזמים — הם מנצחים בדיוק כי אחרים נסוגים',
  '"אני חושב כמו האקר — תמיד מחפש את הפרצה"': '🕵️ בחרת כמו מומחי סייבר — הסקרנות שלהם היא הנשק הגדול ביותר',
  // Q6: learning_style
  'זורק את עצמי למים ולומד תוך כדי': '🛠️ בחרת כמו מפתחים — הקוד הראשון הוא הלמידה הטובה ביותר',
  'קורא, מחקר, מבין לעומק לפני שמתחיל': '📖 בחרת כמו אנליסטים — הם לא זזים בלי להבין את כל התמונה',
  'צופה באחרים ואז מחקה ומשפר': '👀 בחרת כמו מעצבים — הם לומדים מהדוגמאות הטובות ביותר',
  'מקבל פידבק ומשתפר בלולאות מהירות': '💬 בחרת כמו אנשי שיווק — כל קמפיין הוא ניסוי שמלמד אותם',
  // Q7: goal
  'קריירה חדשה לגמרי — פרק חדש': '🚀 בחרת כמו אנשים שלא מפחדים להתחיל מחדש — זה האומץ הגדול ביותר',
  'להיות יותר מקצועי ורלוונטי במה שכבר יש לי': '📈 בחרת כמו אנשים שמשדרגים — הם מכפילים את הערך הקיים שלהם',
  'להרוויח יותר בצד — בלי לזנוח את מה שיש': '💼 בחרת כמו יזמי צד — הם בונים הכנסה נוספת בחכמה',
  'כניסה לאקדמיה — הכנה לבחינות קבלה': '🎓 בחרת כמו אנשים שמשקיעים בעתיד האקדמי שלהם',
  'כניסה לתחום טכנולוגי מוגדר — סייבר, ענן או פיתוח': '💻 בחרת כמו אנשים שיודעים בדיוק לאן הם הולכים — ממוקדים וחדים',
  // Q8: finish
  'יש לי פרויקטים אמיתיים שאפשר להראות': '🧪 בחרת כמו אנשי פורטפוליו — תיק העבודות הוא כרטיס הביקור',
  'יש לי תעודה או הסמכה שמוכרת בשוק': '🎓 בחרת כמו אנשים שמעריכים אישורים רשמיים — זה פותח דלתות',
  'אני כבר מרוויח כסף מהתחום החדש': '💸 בחרת כמו יזמים — הכסף הראשון מהתחום הוא ההוכחה האמיתית',
  'יש לי קהילה ואנשים בתחום שהכרתי': '🤝 בחרת כמו אנשי נטוורקינג — הרשת שלך שווה יותר מכל תעודה',
};

// ─── Background color palette (outside component — stable reference) ─────────
const BG_COLORS = [
  'rgba(200,83,30,0.18)',   // 0: default purple
  'rgba(59, 130, 246, 0.08)',   // 1: programming blue
  'rgba(139, 92, 246, 0.08)',   // 2: data purple
  'rgba(236, 72, 153, 0.08)',   // 3: marketing pink
  'rgba(245, 158, 11, 0.08)',   // 4: ux amber
  'rgba(16, 185, 129, 0.08)',   // 5: stock green
  'rgba(6, 182, 212, 0.08)',    // 6: psychometric cyan
  'rgba(99, 102, 241, 0.08)',   // 7: business indigo
  'rgba(239, 68, 68, 0.08)',    // 8: content red
  'rgba(20, 184, 166, 0.08)',   // 9: service teal
  'rgba(239, 68, 68, 0.10)',    // 10: cyber red-orange
  'rgba(14, 165, 233, 0.08)',   // 11: cloud sky blue
] as const;

const TRACK_COLOR_INDEX: Record<TrackKey, number> = {
  programming: 1, data_ai: 2, digital_marketing: 3, ui_ux: 4,
  stock_market: 5, psychometric: 6, business_management: 7,
  content_creation: 8, customer_service: 9,
  cyber: 10, cloud_devops: 11,
};

// ─────────────────────────────────────────────────────────────────────────────
// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

export default function QuizPage() {
  const router = useRouter();
  const { settings } = useAccessibility();
  const isRTL = I18nManager.isRTL;

  const nameRef = useRef<TextInput>(null);
  const phoneRef = useRef<TextInput>(null);
  const emailRef = useRef<TextInput>(null);

  const [logoUri, setLogoUri] = useState<string | null>(null);
  const [shareCopied, setShareCopied] = useState(false);

  // Animation refs
  const cardAnim = useRef(new Animated.Value(1)).current; // starts at 1: first question visible immediately
  const feedbackAnim = useRef(new Animated.Value(0)).current;
  const progressAnim = useRef(new Animated.Value(0)).current;
  const revealAnim = useRef(new Animated.Value(0)).current;

  const [liveFeedback, setLiveFeedback] = useState<string | null>(null);
  const [points, setPoints] = useState(0);
  const [displayPoints, setDisplayPoints] = useState(0);
  const [bgColorIndex, setBgColorIndex] = useState(0);

  // ✅ FIX: progress dot uses track width in px (perfect sync)
  const DOT_SIZE = 16;
  const [progressWidth, setProgressWidth] = useState(0);

  const dotX = useMemo(() => {
    const max = Math.max(0, progressWidth - DOT_SIZE);
    return progressAnim.interpolate({
      inputRange: [0, 100],
      outputRange: [0, max],
      extrapolate: 'clamp',
    });
  }, [progressAnim, progressWidth]);

  // ─── Page engagement + page view ─────────────────────────────────────────
  usePageEngagement('/quiz');

  useEffect(() => {
    void trackPageView('/quiz');
    void trackFunnelStep('quiz_start');
    void trackButtonClick('quiz_page_load', '/quiz');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const mod = require('@/app/assets/images/kursi-logo-new.png');
        const asset = Asset.fromModule(mod);
        await asset.downloadAsync();
        if (mounted) setLogoUri(asset.uri);
      } catch {
        if (mounted) setLogoUri(null);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // ─── Tracks ────────────────────────────────────────────────────────────────

  const tracks: Record<TrackKey, Track> = useMemo(
    () => ({
      programming: {
        key: 'programming',
        title: 'תכנות ופיתוח',
        subtitle: 'בניית מערכות, פתרון בעיות לוגיים ויצירת כלים שאנשים משתמשים בהם מדי יום.',
        bullets: ['פרויקטים מעשיים ותיק עבודות', 'עבודה עצמאית ומרחוק', 'שכר גבוה כבר בשנה הראשונה'],
        href: '/courses/programming',
        chatSeed: 'קורס תכנות',
        emoji: '💻',
        color: '#3b82f6',
        gradient: ['#1d4ed8', '#3b82f6'],
        persona: 'הבונים',
      },
      data_ai: {
        key: 'data_ai',
        title: 'דאטה ובינה מלאכותית',
        subtitle: 'ניתוח נתונים, מודלים ותובנות — המקצוע הכי מבוקש בעשור הנוכחי.',
        bullets: ['SQL, Python, Machine Learning', 'תובנות עסקיות שמשנות החלטות', 'ביקוש עצום בשוק'],
        href: '/courses/data-ai',
        chatSeed: 'קורס דאטה AI',
        emoji: '🧠',
        color: '#8b5cf6',
        gradient: ['#6d28d9', '#8b5cf6'],
        persona: 'האנליסטים',
      },
      digital_marketing: {
        key: 'digital_marketing',
        title: 'שיווק דיגיטלי',
        subtitle: 'קמפיינים, קריאייטיב ומדידת תוצאות — שילוב מושלם של יצירתיות ונתונים.',
        bullets: ['PPC, SEO, Social Media', 'תוצאות שמדידות ביום הראשון', 'גמישות לעבוד מכל מקום'],
        href: '/courses/digital-marketing',
        chatSeed: 'קורס שיווק דיגיטלי',
        emoji: '📣',
        color: '#ec4899',
        gradient: ['#be185d', '#ec4899'],
        persona: 'המשווקים',
      },
      ui_ux: {
        key: 'ui_ux',
        title: 'עיצוב חוויית משתמש',
        subtitle: 'עיצוב מוצרים דיגיטליים — שם שיצירה פוגשת חשיבה מוצרית.',
        bullets: ['Figma ו-Design Systems', 'מחקר משתמשים אמיתי', 'תיק עבודות ויזואלי מרשים'],
        href: '/courses/ui-ux',
        chatSeed: 'קורס UI UX',
        emoji: '🎨',
        color: '#f59e0b',
        gradient: ['#d97706', '#f59e0b'],
        persona: 'המעצבים',
      },
      stock_market: {
        key: 'stock_market',
        title: 'שוק ההון',
        subtitle: 'מסחר, ניתוח ופיננסים — למי שמרגיש נוח עם סיכון מחושב ואוהב שווקים.',
        bullets: ['ניהול תיקי השקעות', 'ניתוח טכני ופונדמנטלי', 'אסטרטגיות מסחר מתקדמות'],
        href: '/courses/stock-market',
        chatSeed: 'קורס שוק ההון',
        emoji: '📈',
        color: '#10b981',
        gradient: ['#059669', '#10b981'],
        persona: 'הסוחרים',
      },
      psychometric: {
        key: 'psychometric',
        title: 'פסיכומטרי ומכינות',
        subtitle: 'הכנה למבחנים אקדמיים — מסגרת ברורה, יעדים מדידים, תוצאות מוכחות.',
        bullets: ['תוכנית מובנית ומותאמת', 'סימולציות בתנאי מבחן אמיתיים', 'מעקב התקדמות שבועי'],
        href: '/courses/psychometric',
        chatSeed: 'פסיכומטרי ומכינות',
        emoji: '📝',
        color: '#06b6d4',
        gradient: ['#0891b2', '#06b6d4'],
        persona: 'הלומדים',
      },
      business_management: {
        key: 'business_management',
        title: 'ניהול עסקי',
        subtitle: 'הובלת צוותים ופרויקטים — מתאים לאנשים שמניעים אחרים ואוהבים לקבל החלטות.',
        bullets: ['ניהול פרויקטים ומתודולוגיות', 'הובלת צוותים בסביבות משתנות', 'חשיבה אסטרטגית ועסקית'],
        href: '/courses/business-management',
        chatSeed: 'קורס ניהול עסקי',
        emoji: '💼',
        color: '#6366f1',
        gradient: ['#4f46e5', '#6366f1'],
        persona: 'המנהלים',
      },
      content_creation: {
        key: 'content_creation',
        title: 'יצירת תוכן',
        subtitle: 'כתיבה, וידאו וסושיאל — לאנשים יצירתיים שרוצים לספר סיפורים שמזיזים.',
        bullets: ['כתיבה שיווקית ויצירתית', 'עריכת וידאו ורילסים', 'בניית קהילה ומותג אישי'],
        href: '/courses/content-creation',
        chatSeed: 'קורס יצירת תוכן',
        emoji: '🎬',
        color: '#ef4444',
        gradient: ['#dc2626', '#ef4444'],
        persona: 'היוצרים',
      },
      customer_service: {
        key: 'customer_service',
        title: 'שירות ותמיכה',
        subtitle: 'עבודה עם לקוחות ופתרון בעיות — למי שאנרגיה שלו מגיעה מעזרה לאחרים.',
        bullets: ['תקשורת בין-אישית מתקדמת', 'פתרון בעיות תחת לחץ', 'עבודה עם מערכות CRM'],
        href: '/courses/customer-service',
        chatSeed: 'קורס שירות לקוחות',
        emoji: '🤝',
        color: '#14b8a6',
        gradient: ['#0d9488', '#14b8a6'],
        persona: 'העוזרים',
      },
      cyber: {
        key: 'cyber',
        title: 'סייבר ואבטחת מידע',
        subtitle: 'הגנה על מערכות, גילוי חולשות ואבטחת נתונים — אחד המקצועות הכי מבוקשים בישראל.',
        bullets: ['Ethical Hacking ובדיקות חדירה', 'אבטחת רשתות ומערכות', 'ביקוש עצום בהייטק ובממשל'],
        href: '/courses/cyber',
        chatSeed: 'קורס סייבר ואבטחת מידע',
        emoji: '🔒',
        color: '#ef4444',
        gradient: ['#dc2626', '#ef4444'],
        persona: 'המגינים',
      },
      cloud_devops: {
        key: 'cloud_devops',
        title: 'ענן ו-DevOps',
        subtitle: 'תשתיות ענן, אוטומציה ו-CI/CD — הבסיס שעליו רץ כל מוצר דיגיטלי מודרני.',
        bullets: ['AWS, Azure, GCP', 'Docker, Kubernetes, CI/CD', 'שכר מהגבוהים בתעשייה'],
        href: '/courses/cloud-devops',
        chatSeed: 'קורס ענן DevOps',
        emoji: '☁️',
        color: '#0ea5e9',
        gradient: ['#0284c7', '#0ea5e9'],
        persona: 'הארכיטקטים',
      },
    }),
    []
  );

  // ─── Questions ─────────────────────────────────────────────────────────────

  const questions: Question[] = useMemo(
    () => [
      {
        id: 'energy_source',
        title: 'מאיפה מגיעה האנרגיה שלך?',
        subtitle: 'תחשוב על הרגעים שבהם אתה הכי "בפלו".',
        options: [
          {
            label: 'כשאני בונה משהו מאפס',
            emoji: '🔨',
            persona: 'הדרך של המפתחים',
            weights: { programming: 4, ui_ux: 3, content_creation: 2, cloud_devops: 2 },
          },
          {
            label: 'כשאני מגלה תובנה שאחרים פיספסו',
            emoji: '🔍',
            persona: 'הדרך של האנליסטים',
            weights: { data_ai: 4, stock_market: 3, digital_marketing: 2, cyber: 2 },
          },
          {
            label: 'כשאני גורם למישהו להצליח',
            emoji: '🌟',
            persona: 'הדרך של המנהלים',
            weights: { customer_service: 4, business_management: 3, psychometric: 2 },
          },
          {
            label: 'כשאני יוצר משהו שמרגש אנשים',
            emoji: '✨',
            persona: 'הדרך של היוצרים',
            weights: { ui_ux: 4, content_creation: 4, digital_marketing: 2 },
          },
          {
            label: 'כשאני מגן על מערכות ומתמודד עם אתגרים טכניים',
            emoji: '🔒',
            persona: 'הדרך של מומחי הסייבר',
            weights: { cyber: 4, programming: 2, cloud_devops: 3 },
          },
        ],
      },
      {
        id: 'superpower',
        title: 'מה "הסופרפאוור" שלך?',
        subtitle: 'במה אנשים תמיד מבקשים ממך עזרה?',
        options: [
          {
            label: 'לפתור בעיות טכניות שמבלבלות אחרים',
            emoji: '🛠️',
            persona: 'חשיבה הנדסית',
            weights: { programming: 4, data_ai: 2, cloud_devops: 2 },
          },
          {
            label: 'להסביר דברים מורכבים בצורה פשוטה',
            emoji: '💡',
            persona: 'חשיבה מסבירה',
            weights: { customer_service: 3, business_management: 3, content_creation: 2 },
          },
          {
            label: 'לעצב ולמצוא פתרון ויזואלי מושלם',
            emoji: '🎨',
            persona: 'חשיבה ויזואלית',
            weights: { ui_ux: 4, content_creation: 3, digital_marketing: 2 },
          },
          {
            label: 'לנתח מצב ולהחליט נכון תחת לחץ',
            emoji: '📊',
            persona: 'חשיבה אנליטית',
            weights: { data_ai: 4, stock_market: 3, business_management: 2 },
          },
          {
            label: 'לאבטח ולהגן על מערכות מפני איומים',
            emoji: '🛡️',
            persona: 'חשיבת סייבר',
            weights: { cyber: 4, programming: 2, cloud_devops: 2 },
          },
        ],
      },
      {
        id: 'problem_type',
        title: 'איזה סוג בעיה שואב אותך פנימה?',
        subtitle: 'לא מה שאתה טוב בו — מה שמדליק אותך.',
        options: [
          {
            label: 'חידה לוגית שיש לה פתרון אחד נכון',
            emoji: '🧩',
            persona: 'חשיבה לוגית',
            weights: { programming: 4, data_ai: 3, cloud_devops: 2 },
          },
          {
            label: 'בעיה אנושית שאין לה תשובה מושלמת',
            emoji: '💬',
            persona: 'חשיבה אמפתית',
            weights: { customer_service: 4, business_management: 3, ui_ux: 2 },
          },
          {
            label: 'איך להגיע ליעד בצורה הכי חכמה',
            emoji: '🎯',
            persona: 'חשיבה אסטרטגית',
            weights: { business_management: 4, digital_marketing: 3, psychometric: 2 },
          },
          {
            label: 'איך לגרום למשהו להיראות ולהרגיש טוב יותר',
            emoji: '✨',
            persona: 'חשיבה אסתטית',
            weights: { ui_ux: 4, content_creation: 3, digital_marketing: 2 },
          },
          {
            label: 'חולשת אבטחה שצריך לגלות ולסגור לפני שמישהו מנצל אותה',
            emoji: '🔓',
            persona: 'חשיבת האקר אתי',
            weights: { cyber: 4, programming: 2, data_ai: 2 },
          },
        ],
      },
      {
        id: 'work_values',
        title: 'מה הכי חשוב לך בחיים המקצועיים?',
        subtitle: 'בחר את מה שמניע אותך — לא מה שנשמע טוב.',
        options: [
          {
            label: 'חופש — לעבוד מתי ואיך שאני רוצה',
            emoji: '🕊️',
            persona: 'ערך: עצמאות',
            weights: { programming: 3, content_creation: 3, digital_marketing: 2, ui_ux: 2 },
          },
          {
            label: 'השפעה — לדעת שמה שאני עושה משנה',
            emoji: '🤝',
            persona: 'ערך: משמעות',
            weights: { customer_service: 4, business_management: 3, ui_ux: 2 },
          },
          {
            label: 'כסף — צמיחה כלכלית מהירה ומשמעותית',
            emoji: '💰',
            persona: 'ערך: שגשוג',
            weights: { stock_market: 4, digital_marketing: 3, data_ai: 2, programming: 2 },
          },
          {
            label: 'יצירה — ביטוי עצמי בכל מה שאני עושה',
            emoji: '🎭',
            persona: 'ערך: ביטוי',
            weights: { content_creation: 4, ui_ux: 3, digital_marketing: 2 },
          },
          {
            label: 'יציבות — מסגרת ברורה ודרך מוגדרת',
            emoji: '🏛️',
            persona: 'ערך: ביטחון',
            weights: { psychometric: 3, business_management: 2, customer_service: 2 },
          },
        ],
      },
      {
        id: 'self_description',
        title: 'איזה משפט הכי מתאר אותך?',
        subtitle: 'תשובה ראשונה — בלי לחשוב יותר מדי.',
        options: [
          {
            label: '"אני אוהב לנפח בעיות עד שהן נפתרות"',
            emoji: '🔬',
            persona: 'מחשב + שיטתי',
            weights: { data_ai: 4, programming: 3, stock_market: 2 },
          },
          {
            label: '"אני מדבר עם כולם ואנשים פותחים איתי"',
            emoji: '🗣️',
            persona: 'חברתי + מתקשר',
            weights: { customer_service: 4, business_management: 3, digital_marketing: 2 },
          },
          {
            label: '"אני רואה פוטנציאל יצירתי בכל מקום"',
            emoji: '🎨',
            persona: 'יצירתי + חופשי',
            weights: { content_creation: 4, ui_ux: 3, digital_marketing: 2 },
          },
          {
            label: '"אני בונה מערכות ותוכניות לכל דבר"',
            emoji: '📋',
            persona: 'מאורגן + ממוקד',
            weights: { business_management: 4, psychometric: 3, programming: 2 },
          },
          {
            label: '"אני לא מפחד לסכן — הסיכון מרגש אותי"',
            emoji: '🦁',
            persona: 'אמיץ + שוחר סיכון',
            weights: { stock_market: 4, digital_marketing: 2 },
          },
          {
            label: '"אני חושב כמו האקר — תמיד מחפש את הפרצה"',
            emoji: '🕵️',
            persona: 'אנליטי + חשדן במובן הטוב',
            weights: { cyber: 4, programming: 2, data_ai: 2 },
          },
        ],
      },
      {
        id: 'learning_style',
        title: 'איך אתה לומד הכי מהר?',
        subtitle: 'לא מה שנשמע נכון — מה שבאמת עובד עליך.',
        options: [
          {
            label: 'זורק את עצמי למים ולומד תוך כדי',
            emoji: '🛠️',
            persona: 'לומד ממעשה',
            weights: { programming: 4, ui_ux: 3, digital_marketing: 2, content_creation: 2 },
          },
          {
            label: 'קורא, מחקר, מבין לעומק לפני שמתחיל',
            emoji: '📖',
            persona: 'לומד מתיאוריה',
            weights: { data_ai: 3, stock_market: 3, psychometric: 2 },
          },
          {
            label: 'צופה באחרים ואז מחקה ומשפר',
            emoji: '👀',
            persona: 'לומד מצפייה',
            weights: { ui_ux: 3, digital_marketing: 3, business_management: 2 },
          },
          {
            label: 'מקבל פידבק ומשתפר בלולאות מהירות',
            emoji: '💬',
            persona: 'לומד ממשוב',
            weights: { customer_service: 3, business_management: 3, content_creation: 2 },
          },
        ],
      },
      {
        id: 'goal',
        title: 'מה אתה רוצה לשנות בחיים שלך?',
        subtitle: 'זה ישפיע הכי הרבה על ההמלצה.',
        options: [
          {
            label: 'קריירה חדשה לגמרי — פרק חדש',
            emoji: '🚀',
            persona: 'שינוי מוחלט',
            weights: { programming: 3, data_ai: 3, ui_ux: 2, digital_marketing: 2 },
          },
          {
            label: 'להיות יותר מקצועי ורלוונטי במה שכבר יש לי',
            emoji: '📈',
            persona: 'שדרוג קיים',
            weights: { digital_marketing: 3, data_ai: 2, business_management: 3 },
          },
          {
            label: 'להרוויח יותר בצד — בלי לזנוח את מה שיש',
            emoji: '💼',
            persona: 'הכנסה נוספת',
            weights: { content_creation: 3, digital_marketing: 3, customer_service: 2 },
          },
          {
            label: 'כניסה לאקדמיה — הכנה לבחינות קבלה',
            emoji: '🎓',
            persona: 'דרך אקדמית',
            weights: { psychometric: 4 },
          },
          {
            label: 'כניסה לתחום טכנולוגי מוגדר — סייבר, ענן או פיתוח',
            emoji: '💻',
            persona: 'מסלול טכנולוגי ממוקד',
            weights: { cyber: 3, cloud_devops: 3, programming: 2 },
          },
        ],
      },
      {
        id: 'finish',
        title: 'מה הגדרת הצלחה שלך אחרי הקורס?',
        subtitle: 'תחשוב על חצי שנה קדימה.',
        options: [
          {
            label: 'יש לי פרויקטים אמיתיים שאפשר להראות',
            emoji: '🧪',
            persona: 'מוטיבציית תוצרים',
            weights: { programming: 3, data_ai: 3, ui_ux: 3, content_creation: 2 },
          },
          {
            label: 'יש לי תעודה או הסמכה שמוכרת בשוק',
            emoji: '🎓',
            persona: 'מוטיבציית הכרה',
            weights: { psychometric: 4, business_management: 2, customer_service: 2 },
          },
          {
            label: 'אני כבר מרוויח כסף מהתחום החדש',
            emoji: '💸',
            persona: 'מוטיבציית הכנסה',
            weights: { digital_marketing: 4, stock_market: 3, content_creation: 2 },
          },
          {
            label: 'יש לי קהילה ואנשים בתחום שהכרתי',
            emoji: '🤝',
            persona: 'מוטיבציית שייכות',
            weights: { business_management: 3, customer_service: 3, digital_marketing: 2 },
          },
        ],
      },
    ],
    []
  );

  const total = questions.length;

  const [step, setStep] = useState<number>(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isPicking, setIsPicking] = useState(false);

  const [leadCaptured, setLeadCaptured] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadFullName, setLeadFullName] = useState('');
  const [leadPhone, setLeadPhone] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [leadConsent, setLeadConsent] = useState(false);
  const [leadError, setLeadError] = useState<string | null>(null);
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);

  const [selectedTrack, setSelectedTrack] = useState<TrackKey>('digital_marketing');

  const isResults = step === total;
  const current = !isResults ? questions[step] : null;

  // Restore stored lead
  useEffect(() => {
    const stored = safeReadStoredLead();
    if (stored) {
      setLeadFullName(stored.fullName);
      setLeadPhone(stored.phone);
      setLeadEmail(stored.email ?? '');
      setLeadConsent(stored.consent ?? true);
    }
  }, []);

  // ─── Scoring ─────────────────────────────────────────────────────────────

  const computeScores = useCallback(() => {
    const scores: Record<TrackKey, number> = {
      programming: 0, data_ai: 0, digital_marketing: 0, ui_ux: 0,
      stock_market: 0, psychometric: 0, business_management: 0,
      content_creation: 0, customer_service: 0,
      cyber: 0, cloud_devops: 0,
    };
    for (const q of questions) {
      const chosen = answers[q.id];
      if (!chosen) continue;
      const opt = q.options.find((o) => o.label === chosen);
      if (!opt) continue;
      const bonus = q.id === 'goal' || q.id === 'finish' ? 1.3 : 1.0;
      for (const [k, v] of Object.entries(opt.weights)) {
        scores[k as TrackKey] += (v ?? 0) * bonus;
      }
    }
    return scores;
  }, [answers, questions]);

  const top3 = useMemo(() => {
    const scores = computeScores();
    const allValues = Object.values(scores);
    const sorted = (Object.keys(scores) as TrackKey[])
      .map((k) => ({ key: k, score: scores[k] }))
      .sort((a, b) => b.score - a.score);
    const best = sorted[0]?.key ?? 'digital_marketing';
    const second = sorted[1]?.key ?? 'ui_ux';
    const third = sorted[2]?.key ?? 'data_ai';
    return {
      best: { key: best, level: getLevelByZScore(scores[best], allValues), score: Math.round(scores[best]) },
      second: { key: second, level: getLevelByZScore(scores[second], allValues), score: Math.round(scores[second]) },
      third: { key: third, level: getLevelByZScore(scores[third], allValues), score: Math.round(scores[third]) },
    };
  }, [computeScores]);

  // ─── Animations ───────────────────────────────────────────────────────────

  const animateCardIn = useCallback((fromStep: number) => {
    if (fromStep === 0) return; // first question already visible (cardAnim starts at 1)
    if (shouldReduceMotion(settings)) { cardAnim.setValue(1); return; }
    cardAnim.setValue(0);
    Animated.spring(cardAnim, {
      toValue: 1,
      tension: 65,
      friction: 10,
      useNativeDriver: true,
    }).start();
  }, [cardAnim, settings]);

  const animateCardOut = useCallback((onDone: () => void) => {
    if (shouldReduceMotion(settings)) { onDone(); return; }
    Animated.timing(cardAnim, {
      toValue: 0,
      duration: 180,
      useNativeDriver: true,
    }).start(onDone);
  }, [cardAnim, settings]);

  const animateFeedback = useCallback((text: string) => {
    setLiveFeedback(text);
    feedbackAnim.setValue(0);
    Animated.sequence([
      Animated.timing(feedbackAnim, { toValue: 1, duration: 250, useNativeDriver: true }),
      Animated.delay(1800),
      Animated.timing(feedbackAnim, { toValue: 0, duration: 300, useNativeDriver: true }),
    ]).start(() => setLiveFeedback(null));
  }, [feedbackAnim]);

  const animateReveal = useCallback(() => {
    if (shouldReduceMotion(settings)) { revealAnim.setValue(1); return; }
    revealAnim.setValue(0);
    Animated.spring(revealAnim, {
      toValue: 1,
      tension: 50,
      friction: 8,
      useNativeDriver: true,
    }).start();
  }, [revealAnim, settings]);

  // Animate progress bar
  useEffect(() => {
    const pct = isResults ? 100 : ((step + 1) / total) * 100;
    Animated.timing(progressAnim, {
      toValue: pct,
      duration: 400,
      useNativeDriver: false,
    }).start();
  }, [step, isResults, total, progressAnim]);

  // Initial card animation — skip on first mount (step=0, card already visible)
  useEffect(() => {
    animateCardIn(step);
  }, [step, animateCardIn]);

  // Results reveal
  useEffect(() => {
    if (isResults) {
      setSelectedTrack(top3.best.key);
      animateReveal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResults]);

  // Points counter animation — animate displayPoints toward points
  const pointsTargetRef = useRef(0);
  useEffect(() => {
    pointsTargetRef.current = points;
    const interval = setInterval(() => {
      setDisplayPoints((prev) => {
        const target = pointsTargetRef.current;
        if (prev === target) { clearInterval(interval); return prev; }
        return prev + (target > prev ? 1 : -1);
      });
    }, 30);
    return () => clearInterval(interval);
  }, [points]);

  // Update bg color based on leading track
  const updateBgColor = useCallback((newAnswers: Record<string, string>) => {
    const scores: Record<TrackKey, number> = {
      programming: 0, data_ai: 0, digital_marketing: 0, ui_ux: 0,
      stock_market: 0, psychometric: 0, business_management: 0,
      content_creation: 0, customer_service: 0,
      cyber: 0, cloud_devops: 0,
    };
    for (const q of questions) {
      const chosen = newAnswers[q.id];
      if (!chosen) continue;
      const opt = q.options.find((o) => o.label === chosen);
      if (!opt) continue;
      for (const [k, v] of Object.entries(opt.weights)) {
        scores[k as TrackKey] += (v ?? 0);
      }
    }
    const sorted = (Object.keys(scores) as TrackKey[]).sort((a, b) => scores[b] - scores[a]);
    setBgColorIndex(TRACK_COLOR_INDEX[sorted[0]] ?? 0);
  }, [questions]);

  // ─── Navigation ───────────────────────────────────────────────────────────

  const back = () => {
    if (step === 0) return;
    animateCardOut(() => setStep((s) => Math.max(0, s - 1)));
  };

  const goToRead = (k: TrackKey) =>
    router.push({ pathname: '/catalog', params: { topic: tracks[k].title } });
  const goHome = () => router.replace('/');

  const openLeadGate = useCallback(() => {
    setLeadError(null);
    setShowLeadModal(true);
    setTimeout(() => nameRef.current?.focus(), 150);
  }, []);

  useEffect(() => {
    if (isResults && !leadCaptured && !showLeadModal) {
      const tid = setTimeout(() => openLeadGate(), 800);
      return () => clearTimeout(tid);
    }
  }, [isResults, leadCaptured, showLeadModal, openLeadGate]);

  const pick = (q: Question, opt: Option) => {
    if (isPicking) return;
    setIsPicking(true);

    void trackButtonClick('quiz_answer_select', '/quiz', {
      question_id: q.id,
      answer_value: opt.label,
      question_index: step,
    });

    if (Platform.OS !== 'web') {
      try { Vibration.vibrate(40); } catch {}
    }

    const feedbackText = LIVE_FEEDBACK[opt.label];
    if (feedbackText) animateFeedback(feedbackText);

    setPoints((p) => p + 10);

    const newAnswers = { ...answers, [q.id]: opt.label };
    setAnswers(newAnswers);
    updateBgColor(newAnswers);

    const nextStep = step < total - 1 ? step + 1 : total;

    if (nextStep === total) {
      void trackFunnelStep('quiz_complete', { answers_count: Object.keys(newAnswers).length });
      void trackButtonClick('quiz_complete', '/quiz', { total_questions: total });
    }

    animateCardOut(() => {
      setStep(nextStep);
      setIsPicking(false);
    });
  };

  // ─── Chat navigation ──────────────────────────────────────────────────────

  const navigateToChat = useCallback(
    (trackKey?: TrackKey) => {
      const key = trackKey ?? selectedTrack;
      const t = tracks[key];
      router.replace({
        pathname: '/chat',
        params: {
          quizTrack: key,
          quizTrackTitle: t.title,
          quizSource: 'quiz',
          quizLeadCaptured: 'true',
          quizTop: JSON.stringify([top3.best.key, top3.second.key, top3.third.key]),
          quizLevels: JSON.stringify({
            best: top3.best.level,
            second: top3.second.level,
            third: top3.third.level,
          }),
          skipWelcome: 'false',
        },
      });
    },
    [router, selectedTrack, top3, tracks]
  );

  const handleShare = useCallback(async () => {
    const copied = await shareQuizResult(tracks[top3.best.key].title);
    if (copied) {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    }
  }, [top3.best.key, tracks]);

  const restartQuiz = useCallback(() => {
    void trackButtonClick('quiz_restart', '/quiz');
    setStep(0);
    setAnswers({});
    setPoints(0);
    setDisplayPoints(0);
    setBgColorIndex(0);
    // keepLeadCaptured=true so the lead modal doesn't reappear on next completion
  }, []);

  // ─── Lead validation ──────────────────────────────────────────────────────

  const validateLead = (): string | null => {
    const name = leadFullName.trim();
    if (!name || name.length < 2) return 'נא להזין שם מלא (לפחות 2 תווים)';
    const hasPhone = isValidILPhone(leadPhone);
    const hasEmail = isValidEmail(leadEmail);
    if (!hasPhone && !hasEmail) return 'נא להזין טלפון תקין (050...) או כתובת מייל';
    if (leadPhone.trim() && !hasPhone) return 'מספר טלפון לא תקין (דוגמה: 0501234567)';
    if (leadEmail.trim() && !hasEmail) return 'כתובת מייל לא תקינה';
    if (!leadConsent) return 'יש לאשר קבלת פנייה ומידע';
    return null;
  };

  const saveLeadToTable = useCallback(async (): Promise<boolean> => {
    const validationError = validateLead();
    if (validationError) { setLeadError(validationError); return false; }
    if (!canSubmit()) { setLeadError('נא להמתין מספר שניות לפני שליחה חוזרת'); return false; }
    try {
      const safeAnswers = sanitizeAnswers(answers);
      const phone = normalizeILPhone(leadPhone);
      const email = leadEmail.trim().toLowerCase();
      const payload = {
        full_name: leadFullName.trim(),
        phone: phone || null,
        email: email || null,
        consent: true,
        source: 'quiz',
        selected_track: selectedTrack,
        top_tracks: JSON.stringify([top3.best.key, top3.second.key, top3.third.key]),
        answers_json: JSON.stringify(safeAnswers),
      };
      const { data, error } = await supabase
        .from('quiz_leads')
        .insert(payload)
        .select('id')
        .single();
      if (error) {
        if ((error as any).code === '23505') return true;
        setLeadError('שמירה נכשלה. נסה שוב.');
        return false;
      }
      if (!data?.id) { setLeadError('שמירה נכשלה. נסה שוב.'); return false; }
      return true;
    } catch {
      setLeadError('שמירה נכשלה. נסה שוב.');
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, leadConsent, leadEmail, leadFullName, leadPhone, selectedTrack, top3]);

  const submitLeadGate = async () => {
    if (isSubmittingLead) return;
    setIsSubmittingLead(true);
    setLeadError(null);
    const ok = await saveLeadToTable();
    if (!ok) { setIsSubmittingLead(false); return; }
    void trackFunnelStep('lead_captured', { track: top3.best.key, selected_track: selectedTrack });
    void trackButtonClick('quiz_lead_submit', '/quiz', { track: selectedTrack });
    safeWriteStoredLead(leadFullName.trim(), normalizeILPhone(leadPhone), leadEmail.trim().toLowerCase(), true);
    setIsSubmittingLead(false);
    setShowLeadModal(false);

    // Navigate to dedicated results page with quiz context as params
    router.replace({
      pathname: '/quiz/results' as any,
      params: {
        quizTrack: top3.best.key,
        quizTrackTitle: tracks[top3.best.key].title,
        quizTop: JSON.stringify([top3.best.key, top3.second.key, top3.third.key]),
        quizLevels: JSON.stringify({
          best: top3.best.level,
          second: top3.second.level,
          third: top3.third.level,
        }),
        quizLeadCaptured: 'true',
        quizPoints: String(displayPoints),
      },
    });
  };

  const lockedResults = isResults && !leadCaptured;

  // ─── Derived styles ───────────────────────────────────────────────────────

  const dynamic = useMemo(
    () => ({
      pageBg: { backgroundColor: getAccessibleBackgroundColor(theme.colors.background, settings) },
      text: { color: getAccessibleTextColor(theme.colors.text, settings) },
      textLight: { color: getAccessibleTextColor(theme.colors.textLight, settings) },
      surface: {
        backgroundColor: getAccessibleBackgroundColor(theme.colors.surface, settings),
        borderColor: getAccessibleColor(theme.colors.border, settings),
      },
      primaryBg: { backgroundColor: getAccessibleColor(theme.colors.primary, settings) },
      bold: { fontFamily: getAccessibleFontFamily(theme.fonts.bold, settings) },
      regular: { fontFamily: getAccessibleFontFamily(theme.fonts.regular, settings) },
      medium: { fontFamily: getAccessibleFontFamily(theme.fonts.medium, settings) },
    }),
    [settings]
  );

  const progressPct = isResults ? 100 : Math.round(((step + 1) / total) * 100);

  const cardStyle = {
    opacity: cardAnim,
    transform: [
      {
        translateX: cardAnim.interpolate({
          inputRange: [0, 1],
          outputRange: [shouldReduceMotion(settings) ? 0 : 40, 0],
        }),
      },
      {
        scale: cardAnim.interpolate({
          inputRange: [0, 1],
          outputRange: [0.96, 1],
        }),
      },
    ],
  };

  const feedbackStyle = {
    opacity: feedbackAnim,
    transform: [
      {
        translateY: feedbackAnim.interpolate({
          inputRange: [0, 1],
          outputRange: [10, 0],
        }),
      },
    ],
  };

  const revealStyle = {
    opacity: revealAnim,
    transform: [
      {
        translateY: revealAnim.interpolate({
          inputRange: [0, 1],
          outputRange: [30, 0],
        }),
      },
    ],
  };

  const currentBgTint = BG_COLORS[bgColorIndex];

  // ─────────────────────────────────────────────────────────────────────────
  // ─── RENDER ───────────────────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <View style={[styles.page, dynamic.pageBg]}>
      {Platform.OS === 'web' && (
        <Head>
          <meta name="robots" content="noindex, nofollow" />
          <link rel="canonical" href="https://kursiaii.com/quiz" />
        </Head>
      )}

      <Stack.Screen options={{ headerShown: false }} />

      {/* Dynamic background tint */}
      <View style={[styles.bgTint, { backgroundColor: currentBgTint, pointerEvents: 'none' }]} />

      {/* ─── Header ─────────────────────────────────────────────────────── */}
      <View style={styles.header}>
        <Pressable onPress={goHome} style={styles.logoPress}>
          {logoUri ? (
            <SvgUri uri={logoUri} width={120} height={36} accessibilityLabel="לוגו Kursi" />
          ) : (
            <Text style={[styles.logoFallback, dynamic.bold, dynamic.text]}>Kursi</Text>
          )}
        </Pressable>

        {/* Points badge */}
        {!isResults && (
          <View style={styles.pointsBadge}>
            <Text style={[styles.pointsText, dynamic.bold]}>⚡ {displayPoints}</Text>
          </View>
        )}
      </View>

      {/* ─── Progress ───────────────────────────────────────────────────── */}
      {!isResults && (
        <View style={styles.progressSection}>
          <View style={styles.progressRow}>
            <Text style={[styles.progressLabel, dynamic.textLight, dynamic.medium]}>
              שאלה {step + 1} מתוך {total}
            </Text>
            <Text style={[styles.progressPct, dynamic.bold, { color: theme.colors.primary }]}>
              {progressPct}%
            </Text>
          </View>

          <View style={styles.progressWrapper}>
            {/* ✅ measure track width for pixel-perfect dot */}
            <View
              style={styles.progressTrack}
              onLayout={(e) => setProgressWidth(e.nativeEvent.layout.width)}
            >
             <Animated.View
  style={[
    styles.progressFill,
    isRTL ? { right: 0 } : { left: 0 },
    {
      width: progressAnim.interpolate({
        inputRange: [0, 100],
        outputRange: ['0%', '100%'],
        extrapolate: 'clamp',
      }),
    },
  ]}
/>
            </View>

            {/* ✅ dot uses px, no magic 97% */}
            <Animated.View
  style={[
    styles.progressDot,
    isRTL ? { right: dotX } : { left: dotX },
  ]}
/>
          </View>
        </View>
      )}

      {/* ─── Live Feedback Toast ─────────────────────────────────────────── */}
      {!!liveFeedback && (
        <Animated.View style={[styles.feedbackToast, feedbackStyle, { pointerEvents: 'none' }]}>
          <Text style={[styles.feedbackText, dynamic.medium]}>{liveFeedback}</Text>
        </Animated.View>
      )}

      {/* ─── Share copied toast ──────────────────────────────────────────── */}
      {shareCopied && (
        <View style={styles.shareToast}>
          <Text style={[styles.shareToastText, dynamic.bold]}>🔗 הקישור הועתק!</Text>
        </View>
      )}

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.scrollContent,
          Platform.OS === 'web' ? (styles.scrollContentWeb as any) : null,
        ]}
        showsVerticalScrollIndicator={false}
      >
        {/* ─── Question Card ────────────────────────────────────────────── */}
        {!!current && (
          <Animated.View style={[styles.card, dynamic.surface, cardStyle]}>
            {/* Step indicator — centered above question */}
            <View style={styles.stepPill}>
              <Text style={[styles.stepPillText, dynamic.bold, { color: theme.colors.primary }]}>
                שאלה {step + 1} מתוך {total}
              </Text>
            </View>

            <Text style={[styles.qTitle, dynamic.bold, dynamic.text]}>{current.title}</Text>
            {!!current.subtitle && (
              <Text style={[styles.qSub, dynamic.regular, dynamic.textLight]}>{current.subtitle}</Text>
            )}

            <View style={styles.options}>
              {current.options.map((opt, idx) => {
                const isSelected = answers[current.id] === opt.label;
                return (
                  <Pressable
                    key={opt.label}
                    style={({ pressed }) => [
                      styles.optionRow,
                      idx !== current.options.length - 1 ? styles.mb10 : null,
                      isSelected && styles.optionRowSelected,
                      pressed && !shouldReduceMotion(settings) ? styles.pressed : null,
                      isPicking ? styles.disabled : null,
                    ]}
                    onPress={() => pick(current, opt)}
                    disabled={isPicking}
                    accessibilityRole="button"
                    accessibilityLabel={opt.label}
                    accessibilityState={{ selected: isSelected }}
                  >
                    <View style={styles.optionInner}>
                      {!!opt.emoji && (
                        <Text style={styles.optionEmoji}>{opt.emoji}</Text>
                      )}
                      <View style={styles.optionTextWrap}>
                        <Text style={[styles.optionLabel, dynamic.bold, dynamic.text]}>
                          {opt.label}
                        </Text>
                        {!!opt.persona && (
                          <Text style={[styles.optionPersona, dynamic.regular, { color: theme.colors.primary }]}>
                            {opt.persona}
                          </Text>
                        )}
                      </View>
                      {isSelected && (
                        <View style={styles.checkCircle}>
                          <Text style={styles.checkMark}>✓</Text>
                        </View>
                      )}
                    </View>
                  </Pressable>
                );
              })}
            </View>

            {/* Back button */}
            <Pressable
              onPress={back}
              style={[styles.backBtn, step === 0 ? styles.backBtnDisabled : null]}
              disabled={step === 0}
              accessibilityRole="button"
              accessibilityLabel="חזרה"
            >
              <Text style={[styles.backText, dynamic.medium, step === 0 ? styles.backTextDisabled : { color: theme.colors.primary }]}>
                ← חזרה
              </Text>
            </Pressable>
          </Animated.View>
        )}

        {/* ─── Locked Results Teaser ────────────────────────────────────── */}
        {lockedResults && (
          <Animated.View style={[styles.card, dynamic.surface, revealStyle]}>
            <View style={styles.lockWrap}>
              <Text style={styles.lockEmoji}>🎯</Text>
              <View style={[styles.teaserGlow, { backgroundColor: tracks[top3.best.key].color + '20' }]} />
            </View>

            <Text style={[styles.resultsTitle, dynamic.bold, dynamic.text]}>
              התוצאות שלך מוכנות!
            </Text>

            <View style={[styles.teaserCard, { borderColor: tracks[top3.best.key].color + '40', backgroundColor: tracks[top3.best.key].color + '10' }]}>
              <Text style={styles.teaserEmoji}>{tracks[top3.best.key].emoji}</Text>
              <View>
                <Text style={[styles.teaserTrackName, dynamic.bold, { color: tracks[top3.best.key].color }]}>
                  {tracks[top3.best.key].title}
                </Text>
                <Text style={[styles.teaserSub, dynamic.regular, dynamic.textLight]}>
                  ההתאמה הכי חזקה שלך 🥇
                </Text>
              </View>
            </View>

            <Text style={[styles.teaserHint, dynamic.regular, dynamic.textLight]}>
              השאירו פרטים קצרים כדי לפתוח את הניתוח המלא
            </Text>

            <Pressable
              style={({ pressed }) => [
                styles.primaryBtn,
                dynamic.primaryBg,
                pressed && !shouldReduceMotion(settings) ? styles.pressed : null,
              ]}
              onPress={openLeadGate}
              accessibilityRole="button"
            >
              <Text style={[styles.primaryBtnText, dynamic.bold]}>✨ פתח את הניתוח המלא</Text>
            </Pressable>

            <Pressable style={styles.backLink} onPress={back} accessibilityRole="button">
              <Text style={[styles.backLinkText, dynamic.medium, { color: theme.colors.primary }]}>
                ← חזרה לשאלה האחרונה
              </Text>
            </Pressable>
          </Animated.View>
        )}

        {/* ─── Full Results ─────────────────────────────────────────────── */}
        {isResults && leadCaptured && (
          <Animated.View style={[styles.resultsWrap, revealStyle]}>

            {/* Points celebration */}
            <View style={styles.scoreRow}>
              <Text style={[styles.scoreLabelText, dynamic.regular, dynamic.textLight]}>ניקוד השאלון שלך</Text>
              <View style={styles.scoreBadge}>
                <Text style={[styles.scoreNum, dynamic.bold]}>⚡ {displayPoints}</Text>
              </View>
            </View>

            <Text style={[styles.resultsMainTitle, dynamic.bold, dynamic.text]}>
              התוצאות שלך 🎯
            </Text>
            <Text style={[styles.resultsSubTitle, dynamic.regular, dynamic.textLight]}>
              על סמך כל התשובות שלך, אלו התחומים שמתאימים לך
            </Text>

            {/* Top 3 track cards */}
            <View style={styles.trackGrid}>
              {[
                { rank: top3.best, medal: '🥇', rankLabel: 'התאמה הכי חזקה' },
                { rank: top3.second, medal: '🥈', rankLabel: 'התאמה מצוינת' },
                { rank: top3.third, medal: '🥉', rankLabel: 'אופציה מעולה' },
              ].map(({ rank, medal, rankLabel }) => {
                const t = tracks[rank.key];
                const isSelected = selectedTrack === rank.key;
                return (
                  <Pressable
                    key={t.key}
                    style={[
                      styles.trackCard,
                      isSelected && { borderColor: t.color, borderWidth: 2.5 },
                      !isSelected && dynamic.surface,
                    ]}
                    onPress={() => setSelectedTrack(rank.key)}
                    accessibilityRole="button"
                    accessibilityState={{ selected: isSelected }}
                  >
                    <View style={[styles.medalRow, { backgroundColor: t.color + '18' }]}>
                      <Text style={styles.medalEmoji}>{medal}</Text>
                      <Text style={[styles.rankLabel, dynamic.bold, { color: t.color }]}>{rankLabel}</Text>
                    </View>

                    <View style={styles.trackTitleRow}>
                      <Text style={styles.trackEmoji}>{t.emoji}</Text>
                      <Text style={[styles.trackTitle, dynamic.bold, dynamic.text]}>{t.title}</Text>
                    </View>

                    <View style={styles.matchRow}>
                      <View style={[styles.matchBadge, { backgroundColor: t.color + '25' }]}>
                        <Text style={[styles.matchText, dynamic.bold, { color: t.color }]}>
                          התאמה {rank.level}
                        </Text>
                      </View>
                      <Text style={[styles.scoreSmall, dynamic.regular, dynamic.textLight]}>
                        {rank.score} נק׳
                      </Text>
                    </View>

                    <Text style={[styles.trackSub, dynamic.regular, dynamic.textLight]} numberOfLines={2}>
                      {t.subtitle}
                    </Text>

                    <View style={styles.bullets}>
                      {t.bullets.map((b, i) => (
                        <View key={i} style={styles.bulletRow}>
                          <Text style={[styles.bulletCheck, { color: t.color }]}>✓</Text>
                          <Text style={[styles.bulletText, dynamic.regular, dynamic.text]}>{b}</Text>
                        </View>
                      ))}
                    </View>

                    <TouchableOpacity
                      style={[styles.readMoreBtn, { borderColor: t.color, backgroundColor: t.color + '12' }]}
                      onPress={() => goToRead(t.key)}
                      activeOpacity={0.75}
                    >
                      <Text style={[styles.readMoreText, dynamic.bold, { color: t.color }]}>
                        קרא עוד ←
                      </Text>
                    </TouchableOpacity>

                    {isSelected && (
                      <View style={[styles.selectedIndicator, { backgroundColor: t.color }]}>
                        <Text style={styles.selectedIndicatorText}>נבחר</Text>
                      </View>
                    )}
                  </Pressable>
                );
              })}
            </View>

            {/* ─── Dual CTA ──────────────────────────────────────────────── */}
            <View style={[styles.ctaBox, dynamic.surface]}>
              <Text style={[styles.ctaTitle, dynamic.bold, dynamic.text]}>
                בחרת: {tracks[selectedTrack]?.title} {tracks[selectedTrack]?.emoji}
              </Text>
              <Text style={[styles.ctaSub, dynamic.regular, dynamic.textLight]}>
                איך תרצה להמשיך?
              </Text>

              <Pressable
                style={({ pressed }) => [
                  styles.ctaBtnPrimary,
                  dynamic.primaryBg,
                  pressed && !shouldReduceMotion(settings) ? styles.pressed : null,
                ]}
                onPress={() => navigateToChat(selectedTrack)}
                accessibilityRole="button"
              >
                <Text style={[styles.ctaBtnText, dynamic.bold]}>
                  💬 דבר עם הבוט — נמצא יחד את הקורס
                </Text>
                <Text style={[styles.ctaBtnSub, dynamic.regular]}>
                  המלצה מותאמת אישית בשיחה
                </Text>
              </Pressable>

              <TouchableOpacity
                style={[styles.ctaBtnSecondary, { borderColor: tracks[selectedTrack]?.color }]}
                onPress={() => goToRead(selectedTrack)}
                activeOpacity={0.8}
              >
                <Text style={[styles.ctaBtnSecondaryText, dynamic.bold, { color: tracks[selectedTrack]?.color }]}>
                  🔎 מצא לי קורסים בתחום הזה
                </Text>
                <Text style={[styles.ctaBtnSecondarySub, dynamic.regular, dynamic.textLight]}>
                  קטלוג מסונן לפי {tracks[selectedTrack]?.title}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.shareBtn}
                onPress={handleShare}
                activeOpacity={0.75}
                accessibilityRole="button"
                accessibilityLabel={shareCopied ? 'הקישור הועתק' : 'שתף את התוצאות'}
              >
                <Text style={[styles.shareBtnText, dynamic.medium, dynamic.textLight]}>
                  {shareCopied ? '✅ הועתק!' : '🔗 שתף את התוצאות'}
                </Text>
              </TouchableOpacity>
            </View>

            <Pressable
              style={styles.retryBtn}
              onPress={restartQuiz}
              accessibilityRole="button"
              accessibilityLabel="ענה שוב על השאלון"
            >
              <Text style={[styles.retryText, dynamic.medium, { color: theme.colors.textLight }]}>
                ← ענה שוב על השאלון
              </Text>
            </Pressable>

          </Animated.View>
        )}

        <View style={{ height: 24 }} />
      </ScrollView>

      {/* ─── Lead Gate Modal ─────────────────────────────────────────────── */}
      <Modal
        visible={showLeadModal}
        transparent
        animationType="slide"
        statusBarTranslucent
        presentationStyle="overFullScreen"
        accessibilityViewIsModal
        onRequestClose={() => !isSubmittingLead && setShowLeadModal(false)}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.leadOverlay}
        >
          <Pressable
            style={StyleSheet.absoluteFillObject}
            onPress={() => !isSubmittingLead && setShowLeadModal(false)}
          />

          <View style={[styles.leadCard, dynamic.surface]}>
            <View style={[styles.leadTrackHeader, { backgroundColor: tracks[top3.best.key].color + '15' }]}>
              <Text style={styles.leadTrackEmoji}>{tracks[top3.best.key].emoji}</Text>
              <View style={styles.leadTrackTextWrap}>
                <Text style={[styles.leadTrackName, dynamic.bold, { color: tracks[top3.best.key].color }]}>
                  {tracks[top3.best.key].title}
                </Text>
                <Text style={[styles.leadTrackSub, dynamic.regular, dynamic.textLight]}>
                  ההתאמה הכי חזקה שלך
                </Text>
              </View>
              <Pressable
                onPress={() => !isSubmittingLead && setShowLeadModal(false)}
                style={styles.leadCloseBtn}
                disabled={isSubmittingLead}
              >
                <Text style={[styles.leadClose, dynamic.textLight]}>✕</Text>
              </Pressable>
            </View>

            <Text style={[styles.leadTitle, dynamic.bold, dynamic.text]}>
              לאן לשלוח את הניתוח המלא?
            </Text>
            <Text style={[styles.leadSub, dynamic.regular, dynamic.textLight]}>
              נשלח לך המלצות קורסים מותאמות אישית:
            </Text>

            <View style={styles.leadField}>
              <Text style={[styles.leadLabel, dynamic.bold, dynamic.text]}>שם מלא *</Text>
              <TextInput
                ref={nameRef}
                value={leadFullName}
                onChangeText={(t) => { setLeadFullName(t); setLeadError(null); }}
                placeholder="הזן שם מלא"
                placeholderTextColor={theme.colors.textLight}
                style={[styles.leadInput, dynamic.text, dynamic.regular]}
                autoCapitalize="words"
                returnKeyType="next"
                blurOnSubmit={false}
                onSubmitEditing={() => phoneRef.current?.focus()}
                editable={!isSubmittingLead}
              />
            </View>

            <View style={styles.leadField}>
              <Text style={[styles.leadLabel, dynamic.bold, dynamic.text]}>
                טלפון <Text style={[dynamic.regular, dynamic.textLight, { fontSize: 12 }]}>(או מייל — אחד חובה)</Text>
              </Text>
              <TextInput
                ref={phoneRef}
                value={leadPhone}
                onChangeText={(t) => { setLeadPhone(t); setLeadError(null); }}
                placeholder="050-1234567"
                placeholderTextColor={theme.colors.textLight}
                style={[styles.leadInput, dynamic.text, dynamic.regular]}
                keyboardType="phone-pad"
                returnKeyType="next"
                blurOnSubmit={false}
                onSubmitEditing={() => emailRef.current?.focus()}
                editable={!isSubmittingLead}
              />
            </View>

            <View style={styles.leadField}>
              <Text style={[styles.leadLabel, dynamic.bold, dynamic.text]}>
                מייל <Text style={[dynamic.regular, dynamic.textLight, { fontSize: 12 }]}>(אופציונלי אם הזנת טלפון)</Text>
              </Text>
              <TextInput
                ref={emailRef}
                value={leadEmail}
                onChangeText={(t) => { setLeadEmail(t); setLeadError(null); }}
                placeholder="example@email.com"
                placeholderTextColor={theme.colors.textLight}
                style={[styles.leadInput, dynamic.text, dynamic.regular]}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                returnKeyType="done"
                onSubmitEditing={submitLeadGate}
                editable={!isSubmittingLead}
              />
            </View>

            <Pressable
              onPress={() => { if (!isSubmittingLead) { setLeadConsent((v) => !v); setLeadError(null); } }}
              style={styles.consentRow}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: leadConsent }}
              disabled={isSubmittingLead}
            >
              <View style={[styles.consentBox, leadConsent && [styles.consentBoxChecked, { borderColor: theme.colors.primary }]]}>
                {leadConsent && <Text style={styles.consentCheck}>✓</Text>}
              </View>
              <Text style={[styles.consentText, dynamic.regular, dynamic.textLight]}>
                אני מאשר/ת יצירת קשר בנושא קורסים והמלצות מקצועיות
              </Text>
            </Pressable>

            {!!leadError && (
              <View style={styles.errorWrap}>
                <Text style={styles.errorText} accessibilityRole="alert">⚠️ {leadError}</Text>
              </View>
            )}

            <Pressable
              style={({ pressed }) => [
                styles.primaryBtn,
                dynamic.primaryBg,
                isSubmittingLead && styles.btnDisabled,
                pressed && !shouldReduceMotion(settings) && !isSubmittingLead && styles.pressed,
              ]}
              onPress={submitLeadGate}
              disabled={isSubmittingLead}
              accessibilityRole="button"
            >
              {isSubmittingLead ? (
                <View style={styles.loadingRow}>
                  <ActivityIndicator size="small" color="#fff" />
                  <Text style={[styles.primaryBtnText, dynamic.bold]}>שומר...</Text>
                </View>
              ) : (
                <Text style={[styles.primaryBtnText, dynamic.bold]}>✨ פתח את הניתוח המלא</Text>
              )}
            </Pressable>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const isWeb = Platform.OS === 'web';

const styles = StyleSheet.create({
  page: { flex: 1 },
  bgTint: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    pointerEvents: 'none',
  } as any,

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: isWeb ? 28 : 16,
    paddingTop: Platform.OS === 'ios' ? 48 : 12,
    paddingBottom: 8,
  },
  logoPress: { paddingVertical: 4, paddingHorizontal: 4, borderRadius: 999 },
  logoFallback: { fontSize: 20 },

  pointsBadge: {
    backgroundColor: 'rgba(200,83,30,0.18)',
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: 'rgba(200,83,30,0.18)',
  },
  pointsText: { fontSize: 14, color: theme.colors.primary },

  // Progress
  progressSection: {
    paddingHorizontal: isWeb ? 28 : 16,
    paddingBottom: 10,
  },
  progressRow: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  progressLabel: { fontSize: 13 },
  progressPct: { fontSize: 14 },
  progressWrapper: {
    position: 'relative',
    height: 16,
    justifyContent: 'center',
  },
  progressTrack: {
    height: 8,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.07)',
    overflow: 'hidden',
  },
 progressFill: {
  position: 'absolute',
  top: 0,
  bottom: 0,
  borderRadius: 999,
  backgroundColor: theme.colors.primary,
},
  progressDot: {
    position: 'absolute',
    top: '50%',
    marginTop: -8, // -(DOT_SIZE/2)
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: theme.colors.primary,
    borderWidth: 3,
    borderColor: theme.colors.background,
    ...(isWeb ? { boxShadow: `0 0 8px ${theme.colors.primary}` } : {
      shadowColor: theme.colors.primary,
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0.8,
      shadowRadius: 6,
      elevation: 4,
    }),
  } as any,

  // Feedback toast
  feedbackToast: {
    marginHorizontal: isWeb ? 28 : 16,
    marginBottom: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: 'rgba(200,83,30,0.18)',
    borderRadius: 0,
    borderWidth: 1,
    borderColor: 'rgba(200,83,30,0.18)',
  },
  feedbackText: { fontSize: 13, textAlign: 'right', color: theme.colors.text, lineHeight: 18 },

  shareToast: {
    marginHorizontal: isWeb ? 28 : 16,
    marginBottom: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: 'rgba(16,185,129,0.12)',
    borderRadius: 0,
    borderWidth: 1,
    borderColor: 'rgba(16,185,129,0.35)',
    alignItems: 'center',
  },
  shareToastText: { fontSize: 13, color: '#10b981' },

  // Scroll
  scroll: { flex: 1 },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: isWeb ? 28 : 16,
    paddingTop: 8,
    paddingBottom: 32,
    width: '100%',
    alignSelf: 'center',
  },
  scrollContentWeb: { maxWidth: 720, width: '100%', alignSelf: 'center' } as any,

  // Question Card
  card: {
    width: '100%',
    borderWidth: 1,
    borderRadius: 0,
    padding: isWeb ? 32 : 20,
    ...(isWeb
      ? { boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }
      : {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.15,
        shadowRadius: 16,
        elevation: 8,
      }),
  } as any,

  stepPill: {
    alignSelf: 'center',
    borderRadius: 0,
    backgroundColor: 'rgba(200,83,30,0.18)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 6,
    paddingHorizontal: 16,
    marginBottom: 16,
    borderWidth: 1.5,
    borderColor: 'rgba(200,83,30,0.18)',
  },
  stepPillText: { fontSize: 13 },

  qTitle: {
    fontSize: isWeb ? 22 : 19,
    textAlign: 'right',
    lineHeight: isWeb ? 32 : 28,
    marginBottom: 8,
  },
  qSub: {
    fontSize: isWeb ? 14 : 13,
    textAlign: 'right',
    lineHeight: 20,
    marginBottom: 22,
    opacity: 0.8,
  },

  options: { width: '100%' },
  mb10: { marginBottom: 10 },

  optionRow: {
    borderWidth: 1.5,
    borderColor: theme.colors.border,
    borderRadius: 0,
    backgroundColor: 'rgba(255,255,255,0.03)',
    paddingVertical: isWeb ? 16 : 14,
    paddingHorizontal: isWeb ? 18 : 16,
  },
  optionRowSelected: {
    borderColor: theme.colors.primary,
    backgroundColor: 'rgba(200,83,30,0.18)',
  },
  optionInner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  optionEmoji: { fontSize: isWeb ? 24 : 20, width: 32, textAlign: 'center' },
  optionTextWrap: { flex: 1, alignItems: 'flex-end' },
  optionLabel: {
    fontSize: isWeb ? 15 : 14,
    textAlign: 'right',
    lineHeight: 22,
  },
  optionPersona: {
    fontSize: 11,
    textAlign: 'right',
    marginTop: 2,
    opacity: 0.8,
  },
  checkCircle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: theme.colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  checkMark: { color: '#fff', fontSize: 13, fontWeight: '700' },

  pressed: { opacity: 0.86, transform: [{ scale: 0.985 }] },
  disabled: { opacity: 0.55 },

  backBtn: { marginTop: 20, alignSelf: 'flex-end', paddingVertical: 8, paddingHorizontal: 4 },
  backBtnDisabled: { opacity: 0.3 },
  backText: { fontSize: 14 },
  backTextDisabled: { color: theme.colors.textLight },

  // Locked results
  lockWrap: { alignItems: 'center', marginBottom: 14, position: 'relative' },
  lockEmoji: { fontSize: 52, zIndex: 1 },
  teaserGlow: {
    position: 'absolute',
    width: 100,
    height: 100,
    borderRadius: 50,
    top: -20,
    opacity: 0.4,
  },
  resultsTitle: { fontSize: isWeb ? 26 : 22, textAlign: 'center', marginBottom: 16, lineHeight: 34 },
  teaserCard: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    borderWidth: 1.5,
    borderRadius: 0,
    padding: 16,
    marginBottom: 16,
    gap: 14,
  },
  teaserEmoji: { fontSize: 36 },
  teaserTrackName: { fontSize: 18, marginBottom: 4 },
  teaserSub: { fontSize: 13 },
  teaserHint: { fontSize: 14, textAlign: 'center', lineHeight: 22, marginBottom: 18, opacity: 0.8 },

  backLink: { alignSelf: 'center', paddingVertical: 10, paddingHorizontal: 12, marginTop: 8 },
  backLinkText: { fontSize: 14, textDecorationLine: 'underline', textAlign: 'center' },

  // Results
  resultsWrap: { width: '100%' },

  scoreRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  scoreLabelText: { fontSize: 13 },
  scoreBadge: {
    backgroundColor: 'rgba(200,83,30,0.18)',
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: 'rgba(200,83,30,0.18)',
  },
  scoreNum: { fontSize: 15, color: theme.colors.primary },

  resultsMainTitle: { fontSize: isWeb ? 30 : 24, textAlign: 'center', marginBottom: 8, lineHeight: 38 },
  resultsSubTitle: { fontSize: 14, textAlign: 'center', lineHeight: 22, marginBottom: 24, opacity: 0.8 },

  trackGrid: { gap: 16, marginBottom: 24 },

  trackCard: {
    borderWidth: 1.5,
    borderColor: theme.colors.border,
    borderRadius: 22,
    padding: isWeb ? 22 : 18,
    backgroundColor: 'rgba(255,255,255,0.04)',
    position: 'relative',
    overflow: 'hidden',
    ...(isWeb
      ? { boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }
      : {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.1,
        shadowRadius: 10,
        elevation: 4,
      }),
  } as any,

  medalRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 12,
    marginBottom: 14,
    gap: 6,
  },
  medalEmoji: { fontSize: 18 },
  rankLabel: { fontSize: 13, fontWeight: '700' },

  trackTitleRow: { flexDirection: 'row-reverse', alignItems: 'center', marginBottom: 12, gap: 12 },
  trackEmoji: { fontSize: 28 },
  trackTitle: { fontSize: isWeb ? 20 : 18, lineHeight: 26 },

  matchRow: { flexDirection: 'row-reverse', alignItems: 'center', marginBottom: 12, gap: 10 },
  matchBadge: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8 },
  matchText: { fontSize: 13, fontWeight: '700' },
  scoreSmall: { fontSize: 12, opacity: 0.7 },

  trackSub: { fontSize: 13, textAlign: 'right', lineHeight: 20, marginBottom: 14, opacity: 0.8 },

  bullets: { gap: 8, marginBottom: 16 },
  bulletRow: { flexDirection: 'row-reverse', alignItems: 'flex-start', gap: 8 },
  bulletCheck: { fontSize: 15, fontWeight: '700', flexShrink: 0 },
  bulletText: { flex: 1, fontSize: 13, textAlign: 'right', lineHeight: 20 },

  readMoreBtn: {
    borderWidth: 1.5,
    borderRadius: 0,
    paddingVertical: 11,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  readMoreText: { fontSize: 14, fontWeight: '700' },

  selectedIndicator: {
    position: 'absolute',
    top: 0,
    right: 0,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderBottomLeftRadius: 12,
  },
  selectedIndicatorText: { color: '#fff', fontSize: 11, fontWeight: '700' },

  // Dual CTA
  ctaBox: {
    borderWidth: 1,
    borderRadius: 0,
    padding: isWeb ? 28 : 20,
    alignItems: 'center',
    ...(isWeb
      ? { boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }
      : {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.15,
        shadowRadius: 16,
        elevation: 8,
      }),
  } as any,

  ctaTitle: { fontSize: isWeb ? 22 : 19, textAlign: 'center', marginBottom: 6 },
  ctaSub: { fontSize: 14, textAlign: 'center', marginBottom: 20, opacity: 0.75 },

  ctaBtnPrimary: {
    width: '100%',
    borderRadius: 0,
    paddingVertical: 18,
    paddingHorizontal: 20,
    alignItems: 'center',
    marginBottom: 12,
    ...(isWeb
      ? { boxShadow: `0 6px 20px ${theme.colors.primary}50` }
      : {
        shadowColor: theme.colors.primary,
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.35,
        shadowRadius: 12,
        elevation: 6,
      }),
  } as any,
  ctaBtnText: { color: '#fff', fontSize: 16, textAlign: 'center', lineHeight: 22 },
  ctaBtnSub: { color: 'rgba(255,255,255,0.75)', fontSize: 12, textAlign: 'center', marginTop: 4 },

  ctaBtnSecondary: {
    width: '100%',
    borderRadius: 0,
    borderWidth: 2,
    paddingVertical: 16,
    paddingHorizontal: 20,
    alignItems: 'center',
    marginBottom: 16,
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  ctaBtnSecondaryText: { fontSize: 15, textAlign: 'center', lineHeight: 22 },
  ctaBtnSecondarySub: { fontSize: 12, textAlign: 'center', marginTop: 4, opacity: 0.7 },

  shareBtn: {
    paddingVertical: 10,
    paddingHorizontal: 20,
  },
  shareBtnText: { fontSize: 14, textAlign: 'center' },

  retryBtn: { alignSelf: 'center', paddingVertical: 12, paddingHorizontal: 16, marginTop: 4 },
  retryText: { fontSize: 14, textDecorationLine: 'underline', textAlign: 'center' },

  // Primary button
  primaryBtn: {
    width: '100%',
    borderRadius: 0,
    paddingVertical: 17,
    paddingHorizontal: 20,
    alignItems: 'center',
    justifyContent: 'center',
    ...(isWeb
      ? { boxShadow: `0 6px 20px ${theme.colors.primary}45` }
      : {
        shadowColor: theme.colors.primary,
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.35,
        shadowRadius: 12,
        elevation: 6,
      }),
  } as any,
  primaryBtnText: { color: '#fff', fontSize: 16, textAlign: 'center', lineHeight: 22 },
  btnDisabled: { opacity: 0.65 },
  loadingRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10 },

  // Lead Modal
  leadOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.78)',
    justifyContent: 'flex-end',
  },
  leadCard: {
    width: '100%',
    maxWidth: isWeb ? 560 : undefined,
    alignSelf: 'center',
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    padding: 24,
    paddingBottom: Platform.OS === 'ios' ? 40 : 24,
    borderWidth: 1,
    ...(isWeb
      ? { boxShadow: '0 -8px 40px rgba(0,0,0,0.25)' }
      : {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -4 },
        shadowOpacity: 0.2,
        shadowRadius: 20,
        elevation: 12,
      }),
  } as any,

  leadTrackHeader: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    borderRadius: 0,
    padding: 14,
    marginBottom: 18,
    gap: 12,
  },
  leadTrackEmoji: { fontSize: 30 },
  leadTrackName: { fontSize: 16, marginBottom: 2 },
  leadTrackSub: { fontSize: 12 },
  leadTrackTextWrap: { flex: 1 },
  leadCloseBtn: { padding: 6 },
  leadClose: { fontSize: 20 },

  leadTitle: { fontSize: 20, textAlign: 'right', marginBottom: 6, lineHeight: 28 },
  leadSub: { fontSize: 13, textAlign: 'right', lineHeight: 20, marginBottom: 18, opacity: 0.8 },

  leadField: { marginBottom: 14 },
  leadLabel: { fontSize: 14, textAlign: 'right', marginBottom: 6 },
  leadInput: {
    borderWidth: 1.5,
    borderColor: theme.colors.border,
    borderRadius: 0,
    paddingVertical: 13,
    paddingHorizontal: 16,
    textAlign: 'right',
    fontSize: 15,
    backgroundColor: 'rgba(255,255,255,0.04)',
    lineHeight: 20,
    ...(isWeb ? { outlineStyle: 'none' } : {}),
  } as any,

  consentRow: { flexDirection: 'row-reverse', alignItems: 'flex-start', marginTop: 4, marginBottom: 16, gap: 10 },
  consentBox: {
    width: 24,
    height: 24,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: theme.colors.border,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  consentBoxChecked: { backgroundColor: 'rgba(200,83,30,0.18)' },
  consentCheck: { color: theme.colors.primary, fontSize: 14, fontWeight: '700' },
  consentText: { flex: 1, fontSize: 12, textAlign: 'right', lineHeight: 18, opacity: 0.85 },

  errorWrap: {
    backgroundColor: 'rgba(239,68,68,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(239,68,68,0.35)',
    borderRadius: 12,
    padding: 12,
    marginBottom: 14,
  },
  errorText: { color: '#ef4444', fontSize: 13, textAlign: 'right', lineHeight: 18 },
});
